/**
 * API utility functions
 */

/**
 * Clean up query parameters by removing empty, null, or undefined values
 * @param {Object} params - The parameters object to clean
 * @returns {Object} - Cleaned parameters object
 */
export function cleanParams(params) {
  const cleaned = {};
  
  if (!params || typeof params !== 'object') {
    return cleaned;
  }
  
  Object.keys(params).forEach(key => {
    const value = params[key];
    
    // Include parameter if:
    // - It's a number (including 0)
    // - It's a boolean (including false)
    // - It's a non-empty string
    // - It's a non-empty array
    // - It's a valid object (not null)
    if (
      (typeof value === 'number') ||
      (typeof value === 'boolean') ||
      (typeof value === 'string' && value.trim() !== '') ||
      (Array.isArray(value) && value.length > 0) ||
      (value && typeof value === 'object' && !Array.isArray(value))
    ) {
      cleaned[key] = value;
    }
  });
  
  return cleaned;
}

/**
 * Build query string from parameters object, automatically cleaning empty values
 * @param {Object} params - Parameters to convert to query string
 * @returns {string} - Query string (without leading ?)
 */
export function buildQueryString(params) {
  const cleaned = cleanParams(params);
  const searchParams = new URLSearchParams();
  
  Object.keys(cleaned).forEach(key => {
    const value = cleaned[key];
    if (Array.isArray(value)) {
      value.forEach(item => searchParams.append(key, item));
    } else {
      searchParams.append(key, value);
    }
  });
  
  return searchParams.toString();
}

/**
 * Handle API response with consistent error handling
 * @param {Function} apiCall - The API call function to execute
 * @param {string} errorMessage - Default error message if API call fails
 * @returns {Object} - Standardized response object
 */
export async function handleApiResponse(apiCall, errorMessage = 'API request failed') {
  try {
    const response = await apiCall();
    return {
      success: true,
      data: response.data || response
    };
  } catch (error) {
    return {
      success: false,
      message: error?.response?.data?.message || error?.message || errorMessage,
      error: error
    };
  }
}